/**
 * SpacecraftModels - High-fidelity procedural spacecraft
 * Production-quality aerospace engineering aesthetics
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';

// ============================================
// MERCURY RELAY STATION - Advanced Optical Relay
// ============================================

interface MercuryRelayStationProps {
  position: [number, number, number];
  onClick?: () => void;
}

export function MercuryRelayStation({ position, onClick }: MercuryRelayStationProps) {
  const groupRef = useRef<THREE.Group>(null);
  const dishRef = useRef<THREE.Mesh>(null);
  const solarArrayRef = useRef<THREE.Group>(null);
  const antennaRef = useRef<THREE.Group>(null);

  // Slow rotation animation
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
    if (dishRef.current) {
      // Subtle dish tracking motion
      dishRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
    }
    if (solarArrayRef.current) {
      // Solar panels track light
      solarArrayRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.1) * 0.2;
    }
  });

  // Metallic materials
  const primaryMetal = useMemo(() => ({
    color: 0xb8c4ce,
    metalness: 0.9,
    roughness: 0.25,
  }), []);

  const goldFoil = useMemo(() => ({
    color: 0xd4a84b,
    metalness: 0.95,
    roughness: 0.15,
    emissive: 0x332200,
    emissiveIntensity: 0.1,
  }), []);

  const solarPanel = useMemo(() => ({
    color: 0x1a2a4a,
    metalness: 0.3,
    roughness: 0.4,
    emissive: 0x0a1525,
    emissiveIntensity: 0.3,
  }), []);

  return (
    <group
      ref={groupRef}
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {/* === MAIN BUS - Hexagonal Truss Structure === */}
      <group>
        {/* Central hexagonal body */}
        <mesh>
          <cylinderGeometry args={[0.35, 0.35, 0.8, 6]} />
          <meshStandardMaterial {...primaryMetal} />
        </mesh>

        {/* Top cap with thermal louvers */}
        <mesh position={[0, 0.45, 0]}>
          <cylinderGeometry args={[0.38, 0.35, 0.1, 6]} />
          <meshStandardMaterial {...goldFoil} />
        </mesh>

        {/* Bottom cap */}
        <mesh position={[0, -0.45, 0]}>
          <cylinderGeometry args={[0.35, 0.32, 0.1, 6]} />
          <meshStandardMaterial {...primaryMetal} />
        </mesh>

        {/* Thermal louvers (detail strips) */}
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh
            key={`louver-${i}`}
            position={[
              Math.cos((i * Math.PI * 2) / 6) * 0.36,
              0,
              Math.sin((i * Math.PI * 2) / 6) * 0.36
            ]}
            rotation={[0, -(i * Math.PI * 2) / 6, 0]}
          >
            <boxGeometry args={[0.02, 0.6, 0.15]} />
            <meshStandardMaterial color={0x333333} metalness={0.7} roughness={0.5} />
          </mesh>
        ))}
      </group>

      {/* === PRIMARY OPTICAL DISH === */}
      <group ref={dishRef} position={[0, 0, 0.5]}>
        {/* Parabolic dish */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.6, 0.25, 32, 1, true]} />
          <meshStandardMaterial
            color={0xe8e8e8}
            metalness={1}
            roughness={0.05}
            side={THREE.DoubleSide}
          />
        </mesh>

        {/* Dish support ring */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <torusGeometry args={[0.6, 0.02, 8, 32]} />
          <meshStandardMaterial {...primaryMetal} />
        </mesh>

        {/* Feed horn at focus */}
        <mesh position={[0, 0, 0.35]}>
          <coneGeometry args={[0.08, 0.15, 8]} />
          <meshStandardMaterial {...goldFoil} />
        </mesh>

        {/* Feed struts */}
        {[0, 1, 2, 3].map((i) => (
          <mesh
            key={`strut-${i}`}
            position={[
              Math.cos((i * Math.PI * 2) / 4 + Math.PI / 4) * 0.25,
              0,
              Math.sin((i * Math.PI * 2) / 4 + Math.PI / 4) * 0.25 + 0.15
            ]}
            rotation={[Math.PI / 4, (i * Math.PI * 2) / 4, 0]}
          >
            <cylinderGeometry args={[0.008, 0.008, 0.4, 4]} />
            <meshStandardMaterial color={0x666666} metalness={0.8} roughness={0.3} />
          </mesh>
        ))}
      </group>

      {/* === SOLAR ARRAYS === */}
      <group ref={solarArrayRef}>
        {/* Left array */}
        <group position={[-0.8, 0, 0]}>
          {/* Array boom */}
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.5, 8]} />
            <meshStandardMaterial {...primaryMetal} />
          </mesh>

          {/* Solar panel segments */}
          {[-0.3, 0, 0.3].map((yOffset, i) => (
            <mesh key={`left-panel-${i}`} position={[-0.3, yOffset, 0]}>
              <boxGeometry args={[0.5, 0.25, 0.02]} />
              <meshStandardMaterial {...solarPanel} />
            </mesh>
          ))}

          {/* Panel frame */}
          <mesh position={[-0.3, 0, 0]}>
            <boxGeometry args={[0.52, 0.82, 0.01]} />
            <meshStandardMaterial color={0x444444} metalness={0.8} roughness={0.3} />
          </mesh>
        </group>

        {/* Right array (mirrored) */}
        <group position={[0.8, 0, 0]}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.5, 8]} />
            <meshStandardMaterial {...primaryMetal} />
          </mesh>

          {[-0.3, 0, 0.3].map((yOffset, i) => (
            <mesh key={`right-panel-${i}`} position={[0.3, yOffset, 0]}>
              <boxGeometry args={[0.5, 0.25, 0.02]} />
              <meshStandardMaterial {...solarPanel} />
            </mesh>
          ))}

          <mesh position={[0.3, 0, 0]}>
            <boxGeometry args={[0.52, 0.82, 0.01]} />
            <meshStandardMaterial color={0x444444} metalness={0.8} roughness={0.3} />
          </mesh>
        </group>
      </group>

      {/* === HIGH-GAIN ANTENNA === */}
      <group ref={antennaRef} position={[0, 0.6, 0]}>
        {/* Antenna mast */}
        <mesh>
          <cylinderGeometry args={[0.03, 0.03, 0.3, 8]} />
          <meshStandardMaterial {...primaryMetal} />
        </mesh>

        {/* Dish */}
        <mesh position={[0, 0.2, 0]} rotation={[0.3, 0, 0]}>
          <coneGeometry args={[0.15, 0.08, 16, 1, true]} />
          <meshStandardMaterial
            color={0xffffff}
            metalness={1}
            roughness={0.1}
            side={THREE.BackSide}
          />
        </mesh>
      </group>

      {/* === THRUSTER PODS === */}
      {[0, Math.PI].map((rot, i) => (
        <group key={`thruster-${i}`} position={[0, -0.5, 0]} rotation={[0, rot, 0]}>
          <mesh position={[0.25, 0, 0.25]} rotation={[Math.PI / 6, 0, 0]}>
            <coneGeometry args={[0.05, 0.12, 8]} />
            <meshStandardMaterial color={0x333333} metalness={0.9} roughness={0.2} />
          </mesh>
          <mesh position={[0.25, -0.08, 0.25]} rotation={[Math.PI / 6, 0, 0]}>
            <cylinderGeometry args={[0.03, 0.04, 0.03, 8]} />
            <meshStandardMaterial
              color={0xff4400}
              emissive={0xff2200}
              emissiveIntensity={0.5}
            />
          </mesh>
        </group>
      ))}

      {/* === EMISSIVE BEACON === */}
      <mesh position={[0, 0.75, 0]}>
        <sphereGeometry args={[0.03, 16, 16]} />
        <meshBasicMaterial
          color={0x00ff88}
          toneMapped={false}
        />
      </mesh>
      <pointLight position={[0, 0.75, 0]} color={0x00ff88} intensity={2} distance={5} />
    </group>
  );
}


// ============================================
// GEO RECEIVER STATION - Earth Orbital Power Station
// Massive rectenna array for receiving laser power from Mercury relay
// ============================================

interface GEOReceiverStationProps {
  position: [number, number, number];
  onClick?: () => void;
}

export function GEOReceiverStation({ position, onClick }: GEOReceiverStationProps) {
  const groupRef = useRef<THREE.Group>(null);
  const receiverArrayRef = useRef<THREE.Group>(null);
  const solarArrayRef = useRef<THREE.Group>(null);
  const antennaRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.02;
    }
    if (receiverArrayRef.current) {
      receiverArrayRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.15) * 0.03;
    }
    if (solarArrayRef.current) {
      solarArrayRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.08) * 0.1;
    }
    if (antennaRef.current) {
      antennaRef.current.rotation.y = state.clock.elapsedTime * 0.5;
    }
  });

  // Shared materials
  const whiteMetal = { color: 0xe8e8e8, metalness: 0.85, roughness: 0.25 };
  const darkMetal = { color: 0x444444, metalness: 0.9, roughness: 0.3 };
  const copper = { color: 0xb87333, metalness: 0.95, roughness: 0.2 };
  const goldMLI = { color: 0xd4a84b, metalness: 0.9, roughness: 0.2 };
  const solarCell = { color: 0x1a2a4a, metalness: 0.3, roughness: 0.5 };

  return (
    <group
      ref={groupRef}
      position={position}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    >
      {/* ========== CENTRAL TRUSS BACKBONE ========== */}
      <group>
        {/* Main spine - long central truss */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.08, 0.08, 2.5, 8]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Truss cross-members */}
        {[-1, -0.5, 0, 0.5, 1].map((x, i) => (
          <group key={`truss-${i}`} position={[x, 0, 0]}>
            {/* Vertical struts */}
            <mesh>
              <cylinderGeometry args={[0.02, 0.02, 0.4, 6]} />
              <meshStandardMaterial {...darkMetal} />
            </mesh>
            {/* Horizontal struts */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.4, 6]} />
              <meshStandardMaterial {...darkMetal} />
            </mesh>
            {/* Diagonal bracing */}
            <mesh rotation={[Math.PI / 4, 0, Math.PI / 4]} position={[0, 0.1, 0.1]}>
              <cylinderGeometry args={[0.008, 0.008, 0.3, 4]} />
              <meshStandardMaterial color={0x666666} metalness={0.8} roughness={0.3} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ========== MASSIVE RECTENNA ARRAY (Laser Receiver) ========== */}
      <group ref={receiverArrayRef} position={[0, 0, 0.6]}>
        {/* Base dish structure - dark with subtle transparency */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[1.2, 1.2, 0.02, 6]} />
          <meshStandardMaterial
            color={0x112233}
            metalness={0.8}
            roughness={0.3}
            transparent
            opacity={0.85}
          />
        </mesh>

        {/* Energy field layer - glowing transparent */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.025]}>
          <cylinderGeometry args={[1.15, 1.15, 0.01, 6]} />
          <meshBasicMaterial
            color={0x00ffcc}
            transparent
            opacity={0.25}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>

        {/* Hexagonal energy grid lines */}
        {[0.3, 0.6, 0.9].map((radius, i) => (
          <mesh key={`hex-ring-${i}`} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.03]}>
            <torusGeometry args={[radius, 0.008, 4, 6]} />
            <meshBasicMaterial
              color={0x00ffaa}
              transparent
              opacity={0.8}
              toneMapped={false}
            />
          </mesh>
        ))}

        {/* Radial energy lines */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i / 6) * Math.PI * 2;
          return (
            <mesh
              key={`radial-${i}`}
              position={[Math.cos(angle) * 0.6, 0, Math.sin(angle) * 0.6 + 0.03]}
              rotation={[Math.PI / 2, 0, angle]}
            >
              <boxGeometry args={[1.1, 0.015, 0.005]} />
              <meshBasicMaterial
                color={0x00ffaa}
                transparent
                opacity={0.6}
                toneMapped={false}
              />
            </mesh>
          );
        })}

        {/* Active receiver cells - glowing nodes at intersections */}
        {Array.from({ length: 3 }).map((_, ring) => {
          const radius = 0.3 + ring * 0.3;
          return Array.from({ length: 6 }).map((_, i) => {
            const angle = (i / 6) * Math.PI * 2 + (ring % 2) * Math.PI / 6;
            return (
              <group
                key={`node-${ring}-${i}`}
                position={[
                  Math.cos(angle) * radius,
                  0,
                  Math.sin(angle) * radius + 0.04
                ]}
              >
                {/* Node core */}
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                  <cylinderGeometry args={[0.04, 0.04, 0.02, 6]} />
                  <meshStandardMaterial
                    color={0x00ddaa}
                    emissive={0x00ffcc}
                    emissiveIntensity={2}
                    metalness={0.5}
                    roughness={0.3}
                    toneMapped={false}
                  />
                </mesh>
                {/* Node glow */}
                <mesh rotation={[Math.PI / 2, 0, 0]}>
                  <cylinderGeometry args={[0.06, 0.06, 0.01, 6]} />
                  <meshBasicMaterial
                    color={0x00ffcc}
                    transparent
                    opacity={0.5}
                    blending={THREE.AdditiveBlending}
                    toneMapped={false}
                  />
                </mesh>
              </group>
            );
          });
        })}

        {/* Central power converter - bright core */}
        <group position={[0, 0, 0.08]}>
          {/* Converter base */}
          <mesh>
            <cylinderGeometry args={[0.12, 0.18, 0.12, 8]} />
            <meshStandardMaterial
              color={0x00aa77}
              metalness={0.8}
              roughness={0.2}
            />
          </mesh>
          {/* Energy core - super bright */}
          <mesh position={[0, 0.08, 0]}>
            <sphereGeometry args={[0.08, 16, 16]} />
            <meshBasicMaterial
              color={0x00ffdd}
              toneMapped={false}
            />
          </mesh>
          {/* Core glow sphere */}
          <mesh position={[0, 0.08, 0]}>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshBasicMaterial
              color={0x00ffcc}
              transparent
              opacity={0.4}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
          {/* Outer glow */}
          <mesh position={[0, 0.08, 0]}>
            <sphereGeometry args={[0.25, 16, 16]} />
            <meshBasicMaterial
              color={0x00ffaa}
              transparent
              opacity={0.15}
              blending={THREE.AdditiveBlending}
              toneMapped={false}
            />
          </mesh>
        </group>

        {/* Energy conduits from converter */}
        {[0, 1, 2, 3].map((i) => (
          <mesh
            key={`conduit-${i}`}
            position={[Math.cos(i * Math.PI / 2) * 0.22, 0, Math.sin(i * Math.PI / 2) * 0.22 + 0.06]}
            rotation={[Math.PI / 2, 0, 0]}
          >
            <cylinderGeometry args={[0.025, 0.025, 0.1, 6]} />
            <meshStandardMaterial
              color={0x00bb88}
              emissive={0x00ffaa}
              emissiveIntensity={1}
              metalness={0.7}
              roughness={0.3}
              toneMapped={false}
            />
          </mesh>
        ))}

        {/* Outer frame ring - structural */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.22, 0.04, 8, 6]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>

        {/* Frame glow accent */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[1.22, 0.02, 8, 6]} />
          <meshBasicMaterial
            color={0x00ffaa}
            transparent
            opacity={0.3}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
          />
        </mesh>
      </group>

      {/* ========== MASSIVE SOLAR ARRAYS (for station keeping) ========== */}
      <group ref={solarArrayRef}>
        {[-1, 1].map((side) => (
          <group key={`solar-wing-${side}`} position={[side * 1.5, 0, 0]}>
            {/* Main boom */}
            <mesh rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.03, 0.03, 0.8, 6]} />
              <meshStandardMaterial {...whiteMetal} />
            </mesh>

            {/* Solar panel segments - 3 per wing */}
            {[0, 1, 2].map((seg) => (
              <group key={`seg-${seg}`} position={[side * (0.5 + seg * 0.4), 0, 0]}>
                {/* Panel substrate */}
                <mesh>
                  <boxGeometry args={[0.35, 0.6, 0.01]} />
                  <meshStandardMaterial {...solarCell} />
                </mesh>
                {/* Cell grid lines */}
                {[-0.2, -0.1, 0, 0.1, 0.2].map((y, j) => (
                  <mesh key={`gridline-${j}`} position={[0, y, 0.006]}>
                    <boxGeometry args={[0.33, 0.008, 0.002]} />
                    <meshStandardMaterial color={0x334466} metalness={0.6} roughness={0.4} />
                  </mesh>
                ))}
                {/* Panel frame */}
                <mesh position={[0, 0, -0.006]}>
                  <boxGeometry args={[0.37, 0.62, 0.005]} />
                  <meshStandardMaterial {...darkMetal} />
                </mesh>
              </group>
            ))}

            {/* Wing hinges */}
            <mesh position={[side * 0.3, 0, 0]}>
              <cylinderGeometry args={[0.04, 0.04, 0.08, 8]} />
              <meshStandardMaterial {...goldMLI} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ========== HEAT RADIATOR PANELS ========== */}
      {[0, Math.PI].map((rot, i) => (
        <group key={`radiator-${i}`} rotation={[0, rot, 0]} position={[0, 0, -0.3]}>
          {/* Large radiator surface */}
          <mesh position={[0, 0.5, 0]}>
            <boxGeometry args={[0.8, 0.6, 0.01]} />
            <meshStandardMaterial
              color={0x1a1a1a}
              metalness={0.2}
              roughness={0.9}
              emissive={0x220000}
              emissiveIntensity={0.3}
            />
          </mesh>
          <mesh position={[0, -0.5, 0]}>
            <boxGeometry args={[0.8, 0.6, 0.01]} />
            <meshStandardMaterial
              color={0x1a1a1a}
              metalness={0.2}
              roughness={0.9}
              emissive={0x220000}
              emissiveIntensity={0.3}
            />
          </mesh>
          {/* Radiator pipes */}
          {[-0.3, -0.1, 0.1, 0.3].map((x, j) => (
            <mesh key={`radpipe-${j}`} position={[x, 0, 0.01]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.015, 0.015, 1.0, 6]} />
              <meshStandardMaterial {...copper} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ========== COMMAND & CONTROL MODULE ========== */}
      <group position={[-0.8, 0, 0]}>
        {/* Pressurized module */}
        <mesh rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.15, 0.15, 0.5, 16]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>
        {/* End caps */}
        <mesh position={[-0.25, 0, 0]}>
          <sphereGeometry args={[0.15, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>
        <mesh position={[0.25, 0, 0]} rotation={[0, 0, Math.PI]}>
          <sphereGeometry args={[0.15, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial {...whiteMetal} />
        </mesh>
        {/* Windows */}
        {[0, 1, 2].map((i) => (
          <mesh key={`window-${i}`} position={[-0.1 + i * 0.1, 0.14, 0]} rotation={[0, 0, 0]}>
            <circleGeometry args={[0.03, 16]} />
            <meshStandardMaterial color={0x335577} metalness={0.3} roughness={0.5} />
          </mesh>
        ))}
        {/* MLI blankets */}
        <mesh position={[0, -0.12, 0]} rotation={[0, 0, Math.PI / 2]}>
          <boxGeometry args={[0.1, 0.4, 0.02]} />
          <meshStandardMaterial {...goldMLI} />
        </mesh>
      </group>

      {/* ========== MICROWAVE TRANSMITTER TO EARTH ========== */}
      <group position={[0.8, 0, -0.2]}>
        {/* Large phased array */}
        <mesh rotation={[0.3, 0, 0]}>
          <cylinderGeometry args={[0.3, 0.35, 0.08, 16]} />
          <meshStandardMaterial
            color={0xcccccc}
            metalness={0.95}
            roughness={0.1}
          />
        </mesh>
        {/* Array elements */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i / 12) * Math.PI * 2;
          const r = 0.2;
          return (
            <mesh
              key={`txelem-${i}`}
              position={[Math.cos(angle) * r, Math.sin(angle) * r * 0.3 - 0.05, Math.sin(angle) * r]}
            >
              <cylinderGeometry args={[0.025, 0.025, 0.03, 6]} />
              <meshStandardMaterial {...copper} />
            </mesh>
          );
        })}
        {/* Transmission beam indicator */}
        <mesh position={[0, -0.15, 0]}>
          <coneGeometry args={[0.08, 0.2, 8]} />
          <meshBasicMaterial
            color={0x00ffcc}
            transparent
            opacity={0.4}
            blending={THREE.AdditiveBlending}
          />
        </mesh>
      </group>

      {/* ========== COMMUNICATION ANTENNAS ========== */}
      <group ref={antennaRef} position={[0, 0.4, 0]}>
        {/* High-gain dish */}
        <mesh rotation={[0.4, 0, 0]}>
          <coneGeometry args={[0.2, 0.08, 16, 1, true]} />
          <meshStandardMaterial {...whiteMetal} side={THREE.BackSide} />
        </mesh>
        {/* Feed horn */}
        <mesh position={[0, 0.12, -0.08]}>
          <coneGeometry args={[0.03, 0.06, 8]} />
          <meshStandardMaterial {...goldMLI} />
        </mesh>
        {/* Support struts */}
        {[0, 1, 2].map((i) => (
          <mesh
            key={`antstrut-${i}`}
            position={[Math.cos(i * Math.PI * 2 / 3) * 0.08, 0.06, Math.sin(i * Math.PI * 2 / 3) * 0.08 - 0.04]}
            rotation={[0.4, i * Math.PI * 2 / 3, 0]}
          >
            <cylinderGeometry args={[0.005, 0.005, 0.15, 4]} />
            <meshStandardMaterial {...darkMetal} />
          </mesh>
        ))}
      </group>

      {/* ========== DOCKING PORTS ========== */}
      {[{ pos: [-1.1, 0, 0], rot: [0, 0, Math.PI / 2] }, { pos: [0, 0.25, -0.3], rot: [0, 0, 0] }].map((dock, i) => (
        <group key={`dock-${i}`} position={dock.pos as [number, number, number]} rotation={dock.rot as [number, number, number]}>
          <mesh>
            <torusGeometry args={[0.08, 0.015, 8, 16]} />
            <meshStandardMaterial {...whiteMetal} />
          </mesh>
          <mesh>
            <cylinderGeometry args={[0.06, 0.06, 0.04, 16]} />
            <meshStandardMaterial {...darkMetal} />
          </mesh>
          {/* Docking lights */}
          {[0, 1, 2, 3].map((j) => (
            <mesh key={`docklight-${j}`} position={[Math.cos(j * Math.PI / 2) * 0.07, 0.025, Math.sin(j * Math.PI / 2) * 0.07]}>
              <sphereGeometry args={[0.008, 8, 8]} />
              <meshBasicMaterial color={j % 2 === 0 ? 0x00ff00 : 0xff0000} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ========== NAVIGATION LIGHTS ========== */}
      <pointLight position={[1.8, 0, 0]} color={0x00ff00} intensity={1} distance={3} />
      <pointLight position={[-1.8, 0, 0]} color={0xff0000} intensity={1} distance={3} />
      <mesh position={[1.8, 0, 0]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color={0x00ff00} />
      </mesh>
      <mesh position={[-1.8, 0, 0]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color={0xff0000} />
      </mesh>

      {/* Main station glow */}
      <pointLight position={[0, 0, 0.6]} color={0x00ffaa} intensity={3} distance={8} />
    </group>
  );
}


// ============================================
// EAHP SATELLITE UNIT - Individual Swarm Member
// ============================================

export function EAHPSatelliteModel() {
  const groupRef = useRef<THREE.Group>(null);

  return (
    <group ref={groupRef} scale={0.1}>
      {/* Heat shield (sun-facing) */}
      <mesh position={[0, 0, 0.3]}>
        <coneGeometry args={[0.8, 0.3, 6]} />
        <meshStandardMaterial
          color={0xcccccc}
          metalness={0.8}
          roughness={0.4}
        />
      </mesh>

      {/* Main body */}
      <mesh>
        <cylinderGeometry args={[0.4, 0.4, 0.5, 6]} />
        <meshStandardMaterial
          color={0x888888}
          metalness={0.7}
          roughness={0.35}
        />
      </mesh>

      {/* Laser emitter */}
      <mesh position={[0, 0, -0.4]}>
        <cylinderGeometry args={[0.2, 0.25, 0.3, 8]} />
        <meshStandardMaterial
          color={0x00ff88}
          emissive={0x00ff44}
          emissiveIntensity={2}
          metalness={0.5}
          roughness={0.3}
        />
      </mesh>

      {/* Solar concentrator wings */}
      {[-1, 1].map((side) => (
        <mesh key={side} position={[side * 0.8, 0, 0]} rotation={[0, 0, side * Math.PI / 6]}>
          <boxGeometry args={[0.6, 0.02, 0.4]} />
          <meshStandardMaterial
            color={0xffd700}
            metalness={0.95}
            roughness={0.1}
          />
        </mesh>
      ))}
    </group>
  );
}
